"use client";

/**
 * Shared plumbing for the NewForm design-system animated icons.
 *
 * Each icon is a hand-drawn SVG whose strokes "draw on" when the icon
 * mounts (and, for accent parts, a small pop/translate). Color defaults
 * to DS brand purple via `text-nf-ds-purple-product` and uses
 * `currentColor` for strokes, so any icon can be recolored with a text
 * color utility:
 *
 *   import { UploadFileIcon } from "@/components/ui/design-system/icons";
 *
 *   <UploadFileIcon size={16} />                       // brand purple
 *   <DocumentIcon size={20} className="text-muted-foreground" />
 *
 * Motion respects `prefers-reduced-motion`: when set, icons render in
 * their final state with no animation.
 */

import { motion, type Transition, useReducedMotion } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface AnimatedIconProps {
  /** Pixel size of the (square) icon. Default 16. */
  size?: number;
  /** Extra classes — typically a `text-*` color override. */
  className?: string;
}

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Shared SVG shell for the animated icons. Locks the box to an explicit
 * square via both attributes and CSS `width`/`height`, and renders as a
 * non-shrinking block — so the icon reserves its space on first paint and
 * the draw-on animation can never shift surrounding layout.
 */
export function IconSvg({
  size = 16,
  className,
  children,
}: AnimatedIconProps & { children: ReactNode }) {
  return (
    <motion.svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size }}
      className={cn("block shrink-0 text-nf-ds-purple-product", className)}
      aria-hidden="true"
    >
      {children}
    </motion.svg>
  );
}

/** True when the user has requested reduced motion. */
export function useReduce(): boolean {
  return useReducedMotion() ?? false;
}

/** Stroke "draw-on" motion props for a path/line/rect/circle. */
export function drawProps(reduce: boolean, delay = 0) {
  if (reduce) {
    return { initial: false as const, animate: { pathLength: 1, opacity: 1 } };
  }
  return {
    initial: { pathLength: 0, opacity: 0 },
    animate: { pathLength: 1, opacity: 1 },
    transition: { duration: 0.7, delay, ease: EASE } satisfies Transition,
  };
}

/** Fade + slight upward translate for an accent group (e.g. an arrow). */
export function riseProps(reduce: boolean, delay = 0) {
  if (reduce) {
    return { initial: false as const, animate: { y: 0, opacity: 1 } };
  }
  return {
    initial: { y: 2, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    transition: { duration: 0.4, delay, ease: EASE } satisfies Transition,
  };
}
